import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ClientCard from "./ClientCard";
import TestResultsModal from "./TestResultsModal";
import {
  formatDateRangeForZone,
  parseBackendDate,
  USER_TIMEZONE,
} from "../../utils/dateUtils";
import { getUserTestResults } from "../../redux/testSlice";

export default function TherapistAppointments() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const therapistState = useSelector((state) => state.therapist) || {};
  const { therapist } = therapistState;
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fetchedUserResponses, setFetchedUserResponses] = useState([]);
  const [testResultsLoading, setTestResultsLoading] = useState(false);
  const [testResultsError, setTestResultsError] = useState(null);

  // Kullanıcı kartına tıklandığında modal'ı aç
  const handleUserClick = async (userInfo) => {
    setFetchedUserResponses([]);
    setTestResultsError(null);
    setSelectedUser(userInfo);
    setIsModalOpen(true);

    if (!userInfo?.userId) {
      return;
    }

    setTestResultsLoading(true);
    try {
      const result = await dispatch(getUserTestResults(userInfo.userId)).unwrap();
      setFetchedUserResponses(result?.data || []);
    } catch (error) {
      setTestResultsError(
        typeof error === "string"
          ? error
          : error?.message || "Danisan test sonuclari getirilemedi."
      );
    } finally {
      setTestResultsLoading(false);
    }
  };

  // Modal'ı kapat
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setFetchedUserResponses([]);
    setTestResultsLoading(false);
    setTestResultsError(null);
  };
  
  // Gerçek randevuları appointment formatına dönüştür
  const appointments = therapist?.requests?.filter(req => {
    // Sadece userId'si olan ve object tipinde olan istekleri al
    if (!req.userId || typeof req.userId !== 'object') return false;
    
    // Tarih hesaplamaları
    const now = new Date();
    const slotInfo = req.availabilityIds && req.availabilityIds.length > 0
      ? req.availabilityIds[0]
      : null;

    const appointmentDate = slotInfo
      ? parseBackendDate(slotInfo.startDate)
      : req.selectedSlot?.startTime
        ? parseBackendDate(req.selectedSlot.startTime)
        : parseBackendDate(req.createdAt);

    const appointmentEndDate = slotInfo
      ? parseBackendDate(slotInfo.endDate)
      : req.selectedSlot?.endTime
        ? parseBackendDate(req.selectedSlot.endTime)
        : new Date(appointmentDate.getTime() + 60 * 60 * 1000);

    // Randevu seans bitimine kadar görünür kalsın
    return appointmentEndDate > now;
  })?.map(request => {
    // Tarih hesaplamaları
    const now = new Date();
    const createdDate = parseBackendDate(request.createdAt);

    const slotInfo = request.availabilityIds && request.availabilityIds.length > 0
      ? request.availabilityIds[0]
      : null;

    const appointmentDate = slotInfo
      ? parseBackendDate(slotInfo.startDate)
      : request.selectedSlot?.startTime
        ? parseBackendDate(request.selectedSlot.startTime)
        : createdDate;

    const appointmentEndDate = slotInfo
      ? parseBackendDate(slotInfo.endDate)
      : request.selectedSlot?.endTime
        ? parseBackendDate(request.selectedSlot.endTime)
        : new Date(appointmentDate.getTime() + 60 * 60 * 1000);
    
    // Durum hesaplama
    let timeStatus = "";
    let statusColor = "";
    
    // Status'e göre durum belirleme
    if (request.status === "completed") {
      timeStatus = t("completed");
      statusColor = "bgGreen";
    } 
    else if (request.status === "active" || request.status === "ongoing") {
      timeStatus = t("start");
      statusColor = "bgGreen";
    }
    else if (request.status === "pending") {
      // Randevu zamanı geçtiyse
      if (now > appointmentEndDate) {
        timeStatus = t("completed");
        statusColor = "bgGreen";
      } 
      // Şu an randevu süresindeyse
      else if (now >= appointmentDate && now <= appointmentEndDate) {
        timeStatus = t("start");
        statusColor = "bgGreen";
      } 
      // Randevu yaklaşıyorsa
      else {
        // Kalan süre hesaplama
        const diffMs = appointmentDate - now;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (diffHrs > 0) {
          timeStatus = `${diffHrs}h:${diffMins}m`;
        } else if (diffMins > 0) {
          timeStatus = `${diffMins}m`;
        } else {
          timeStatus = t("start");
          statusColor = "bgGreen";
        }
        
        if (!statusColor) {
          statusColor = "bgRed"; // Yaklaşan randevu için kırmızı
        }
      }
    }
    else {
      timeStatus = t(request.status) || request.status;
      statusColor = "bgRed";
    }

    // Tarih formatı 
    const dateStr = formatDateRangeForZone(
      appointmentDate,
      appointmentEndDate,
      USER_TIMEZONE
    );

    const meetingInfo = request.meetingId && typeof request.meetingId === 'object'
      ? request.meetingId
      : null;

    const meetingUrl =
      meetingInfo?.meetingUrl ||
      meetingInfo?.joinUrl ||
      meetingInfo?.url ||
      request.sessionLink ||
      request.meetingUrl ||
      request.meetingLink ||
      "";

    return {
      client: {
        // Populate edilmiş userId objesinden kullanıcı bilgilerini al
        name: `${request.userId.name || 'Bilinmeyen'} ${request.userId.surname || 'Kullanıcı'}`,
        sessionType: t("therapySession") || "therapySession",
        sessionLink: meetingUrl,
        // Eğer userDetails varsa oradan email/profileImage al, yoksa boş bırak
        email: request.userDetails?.email || request.userId.email || "Email yok",
        profileImage: request.userDetails?.profileImage || request.userId.profileImage || "",
        userId: request.userId._id, // UserId'yi de ekle
        // User bilgilerini modal için ekle
        userInfo: {
          ...request.userId,
          userId: request.userId._id,
          ...(meetingUrl ? { sessionLink: meetingUrl } : {}),
        }
      },
      time: dateStr,
      status: timeStatus,
      statusColor: statusColor,
      textColor: statusColor === "bgRed" ? "#FFFFFF" : "#000000",
      originalStatus: request.status,
      requestId: request.requestId,
      userId: request.userId._id,
      onUserClick: handleUserClick // Click handler'ı ekle
    };
  }) || [];

  // Sadece gerçek veriler varsa onları göster, yoksa boş liste
  const displayAppointments = appointments.length > 0 ? appointments : [];

  return (
    <>
      <div className="md:mt-12 mt-[19px] mb-6 bg-cards-bg  rounded-[20px] md:rounded-[50px] shadow-md md:w-[1069px] md:h-[443px] w-[166px] h-[493px] p-4 notranslate" translate="no">
        <h2 className="text-center md:text-[20px] text-[12px]  font-poppins md:mb-4 mb-2 text-text">
          <span>{t("upcomingAppointments")}</span>
        </h2>
        <div className="flex flex-row justify-start gap-4 overflow-x-auto font-poppins items-center">
          {displayAppointments.length > 0 ? (
            displayAppointments.map((appointment, index) => (
              <ClientCard key={appointment.requestId || index} {...appointment} />
            ))
          ) : (
            <div className="text-center w-full text-text font-poppins opacity-70">
              <span>{t("noUpcomingAppointments") || "Yaklaşan randevu bulunmuyor"}</span>
            </div>
          )}
        </div>
      </div>

      {/* Test Results Modal */}
      <TestResultsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        userInfo={selectedUser}
        fetchedUserResponses={fetchedUserResponses}
        loading={testResultsLoading}
        error={testResultsError}
      />
    </>
  );
}
